/**
 * Namespace containing events in this app.
 */
export const NAMESPACE = 'nudge';

/**
 * Events that are sent from UI thread to service worker.
 */
export enum BgEventType {
  Logout = 'logout',
  Update = 'update',
  AccessToken = 'accessToken',
}

/**
 * Events that are sent from the service worker to the UI thread.
 */
export enum FgEventType {
  MsgReceive = 'msg.receive',
  MsgStatus = 'msg.status',
  MsgSend = 'msg.send',
  SysLogout = 'sys.logout',
  SysInit = 'sys.init',
}

/**
 * Direction of posted message.
 */
export enum Direction {
  Fg = 'fg',
  Bg = 'bg',
}

/**
 * Create a new message.
 */
const newMessage = <
  U extends BgEventType | FgEventType,
  T extends Record<string, unknown>,
  V extends Direction,
>(
  eventType: U,
  payload: T,
  direction: V,
) => ({
  ts: Date.now(),
  namespace: NAMESPACE as typeof NAMESPACE,
  message: eventType,
  payload,
  direction,
});

/**
 * Creat a new background event.
 */
export const NewBgEvent = <T extends Record<string, unknown>>(
  eventType: BgEventType,
  payload: T,
): BgEvent<T> => newMessage(eventType, payload, Direction.Bg);

/**
 * Create a new foreground event.
 */
export const NewFgEvent = <T extends Record<string, unknown>>(
  eventType: FgEventType,
  payload: T,
): FgEvent<T> => newMessage(eventType, payload, Direction.Fg);

/**
 * Shared properties of nudge events.
 */
type BaseNudgeEvent<T extends Record<string, unknown>> = {
  payload: T;
  ts: number;
  namespace: typeof NAMESPACE;
};

/**
 * A background event.
 */
export type BgEvent<T extends Record<string, unknown>> = BaseNudgeEvent<T> & {
  direction: Direction.Bg;
  message: BgEventType;
};

/**
 * A foreground event.
 */
export type FgEvent<T extends Record<string, unknown>> = BaseNudgeEvent<T> & {
  direction: Direction.Fg;
  message: FgEventType;
};

/**
 * Check if event is background event.
 */
export const isBgEvent = (
  evt: unknown,
): evt is BgEvent<Record<string, unknown>> =>
  evt instanceof Object && 'direction' in evt && evt.direction === Direction.Bg;

/**
 * Check if event is foreground event.
 */
export const isFgEvent = (
  evt: unknown,
): evt is FgEvent<Record<string, unknown>> =>
  evt instanceof Object && 'direction' in evt && evt.direction === Direction.Fg;

/**
 * Any event.
 */
export type WorkerEvent<T extends Record<string, unknown>> =
  | BgEvent<T>
  | FgEvent<T>;
